import type { CertificationDocument } from 'src/types/CertificationDocumentTypes';

export enum DOCUMENT_POSITION {
  UPPER = 'upper',
  LOWER = 'lower',
  NONE = null,
}

export type FlatRow = {
  id: number;
  parent: number | null;
  position: DOCUMENT_POSITION;
  document: CertificationDocument;
};

/**
 * Flattens a nested document tree into a flat array containing parent and all children.
 * This maintains the display order: upper children, parent, lower children.
 */
export const flattenFMDocuments = (
  docs: CertificationDocument[]
): CertificationDocument[] => {
  if (!Array.isArray(docs)) {
    return [];
  }

  const flattened: CertificationDocument[] = [];
  for (const parent of docs) {
    // Add upper children first
    if (parent.upper_children_certification_documents?.length) {
      flattened.push(...parent.upper_children_certification_documents);
    }
    // Add parent
    flattened.push(parent);
    // Add lower children last
    if (parent.lower_children_certification_documents?.length) {
      flattened.push(...parent.lower_children_certification_documents);
    }
  }
  return flattened;
};

/**
 * Flattens the documents that go into a preview/download package so the page
 * order follows the order arranged in the File Manager (ENG-3852).
 *
 * `documents` is what the package should contain (the selection, or the
 * displayed documents when nothing is selected). `arrangedDocuments` is the
 * full arranged tree (renderedDocuments). Requested documents are emitted in
 * arranged order, each at most once: a parent and its child can both be
 * selected, and the child must not appear twice in the merged PDF. Anything
 * requested but missing from the arranged tree (hidden by a filter, or already
 * removed) is appended afterwards so it is not silently dropped.
 */
export const buildOrderedPackageDocuments = (
  documents: CertificationDocument[],
  arrangedDocuments: CertificationDocument[]
): CertificationDocument[] => {
  if (!Array.isArray(documents) || documents.length === 0) {
    return [];
  }

  const requested = flattenFMDocuments(documents);
  const requestedIds = new Set(requested.map((doc) => doc.id));
  const emittedIds = new Set<number>();
  const ordered: CertificationDocument[] = [];

  const emit = (doc: CertificationDocument): void => {
    if (!requestedIds.has(doc.id) || emittedIds.has(doc.id)) {
      return;
    }
    emittedIds.add(doc.id);
    ordered.push(doc);
  };

  const emitTree = (trees: CertificationDocument[]): void => {
    for (const parent of trees) {
      for (const child of parent.upper_children_certification_documents ||
        []) {
        emit(child);
      }
      emit(parent);
      for (const child of parent.lower_children_certification_documents ||
        []) {
        emit(child);
      }
    }
  };

  emitTree(arrangedDocuments || []);
  emitTree(documents);

  return ordered;
};

/**
 * Computes a flat list of documents with metadata for rendering and drag-and-drop.
 * Each item includes the document, its parent ID, and its position relative to the parent.
 */
export const computeFlatFrom = (docs: CertificationDocument[]): FlatRow[] => {
  if (!Array.isArray(docs)) {
    return [];
  }

  const rows: FlatRow[] = [];
  for (const parent of docs) {
    for (const child of parent.upper_children_certification_documents || []) {
      rows.push({
        id: child.id,
        parent: parent.id,
        position: DOCUMENT_POSITION.UPPER,
        document: child,
      });
    }
    rows.push({
      id: parent.id,
      parent: null,
      position: DOCUMENT_POSITION.NONE,
      document: parent,
    });
    for (const child of parent.lower_children_certification_documents || []) {
      rows.push({
        id: child.id,
        parent: parent.id,
        position: DOCUMENT_POSITION.LOWER,
        document: child,
      });
    }
  }
  return rows;
};

export const convertFlatToNested = (
  flat: FlatRow[],
  originals: CertificationDocument[],
  movedDocumentId?: number | null
): CertificationDocument[] => {
  const cloneDoc = (doc: CertificationDocument): CertificationDocument => {
    return {
      ...doc,
      parent: null,
      upper_children_certification_documents: [],
      lower_children_certification_documents: [],
    };
  };

  // Build map of all original docs by id (including nested children)
  const allDocs: Record<number, CertificationDocument> = {};
  const stack: CertificationDocument[] = [...originals];
  while (stack.length > 0) {
    const d = stack.pop() as CertificationDocument;
    if (!allDocs[d.id]) {
      allDocs[d.id] = d;
      if (Array.isArray(d.upper_children_certification_documents)) {
        stack.push(...d.upper_children_certification_documents);
      }
      if (Array.isArray(d.lower_children_certification_documents)) {
        stack.push(...d.lower_children_certification_documents);
      }
    }
  }

  // Clone to avoid mutating originals
  const idToClone: Record<number, CertificationDocument> = {};
  Object.values(allDocs).forEach((doc) => {
    idToClone[doc.id] = cloneDoc(doc);
  });

  // Parents appear in flat list with position === null (NONE)
  const parentsInOrder: CertificationDocument[] = [];
  for (const row of flat) {
    if (row.position === DOCUMENT_POSITION.NONE) {
      const parentClone = idToClone[row.id];
      if (parentClone) {
        parentClone.parent = null;
        parentsInOrder.push(parentClone);
      }
    }
  }

  // Attach children to parents in listed order
  for (const row of flat) {
    if (
      row.position === DOCUMENT_POSITION.UPPER ||
      row.position === DOCUMENT_POSITION.LOWER
    ) {
      const childClone = idToClone[row.id];
      const parentId = row.parent;
      if (!childClone || parentId == null) {
        continue;
      }
      const parentClone = idToClone[parentId];
      if (!parentClone) {
        continue;
      }
      childClone.parent = parentId;
      if (row.position === DOCUMENT_POSITION.UPPER) {
        parentClone.upper_children_certification_documents.push(childClone);
      } else {
        parentClone.lower_children_certification_documents.push(childClone);
      }
    }
  }

  let orderCounter = 0;
  parentsInOrder.forEach((parent) => {
    const originalDoc = allDocs[parent.id];
    const uppers = parent.upper_children_certification_documents || [];
    uppers.forEach((child, idx) => {
      const originalChild = allDocs[child.id];
      const newSubOrder = idx;
      const newOrder = orderCounter++;
      child.sub_order = newSubOrder;
      child.order = newOrder;
      const childOrderChanged = child.id === movedDocumentId;
      child.has_order_changed =
        childOrderChanged || originalChild.has_order_changed;
    });

    const newParentOrder = orderCounter++;
    parent.order = newParentOrder;
    parent.sub_order = null;
    const parentOrderChanged = parent.id === movedDocumentId;
    parent.has_order_changed =
      parentOrderChanged || originalDoc.has_order_changed;

    const lowers = parent.lower_children_certification_documents || [];
    lowers.forEach((child, idx) => {
      const originalChild = allDocs[child.id];
      const newSubOrder = idx;
      const newOrder = orderCounter++;
      child.sub_order = newSubOrder;
      child.order = newOrder;
      const childOrderChanged = child.id === movedDocumentId;
      child.has_order_changed =
        childOrderChanged || originalChild.has_order_changed;
    });
  });

  return parentsInOrder;
};

export type OrderDataItem = {
  order: number;
  sub_order: number | null;
  position: 'upper' | 'lower' | null;
  parent_id: number | null;
  has_order_changed?: boolean;
  is_editable?: boolean;
  id: number;
};

export const convertNestedToOrderData = (
  documents: CertificationDocument[]
): OrderDataItem[] => {
  const orderData: OrderDataItem[] = [];
  let orderCounter = 0;

  documents.forEach((parent) => {
    // Add upper children first
    if (parent.upper_children_certification_documents?.length) {
      parent.upper_children_certification_documents.forEach(
        (upperChild, index) => {
          orderData.push({
            order: orderCounter++,
            sub_order: index,
            position: 'upper',
            parent_id: parent.id,
            has_order_changed: upperChild.has_order_changed || false,
            id: upperChild.id,
          });
        }
      );
    }

    // Add parent document
    orderData.push({
      order: orderCounter++,
      sub_order: null,
      position: null,
      parent_id: null,
      has_order_changed: parent.has_order_changed || false,
      id: parent.id,
    });

    // Add lower children last
    if (parent.lower_children_certification_documents?.length) {
      parent.lower_children_certification_documents.forEach(
        (lowerChild, index) => {
          orderData.push({
            order: orderCounter++,
            sub_order: index,
            position: 'lower',
            parent_id: parent.id,
            has_order_changed: lowerChild.has_order_changed || false,
            id: lowerChild.id,
          });
        }
      );
    }
  });

  return orderData;
};
